/* eslint-disable */
import { useState, useEffect } from "react";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import Img from "./Img";
import { CATEGORIES } from "./constants";
import { searchPhoto } from "../utils/pexels";
import { COLOR, FONT } from "../utils/theme";
import { Icon } from "../utils/icons";
import useWindowWidth from "../utils/useWindowWidth";
import { getCostRange } from "../utils/costHelpers";

export default function ActivityCard({ act, isAlt = false, onClick, onEdit, onActivityUpdate, dayCity, dayCountry }) {
  const { isDesktop } = useWindowWidth();
  const [pressed, setPressed] = useState(false);
  const [photo, setPhoto] = useState(act.img || null);

  useEffect(() => {
    const src = act.img || "";
    if (src && !src.includes("picsum")) { setPhoto(src); return; }
    const locName = (act.location?.name || "").replace(/\(.*?\)/g, "").trim();
    const query = [locName, dayCity].filter(Boolean).join(" ") || act.title;
    searchPhoto(query, act.img).then(url => {
      if (!url) return;
      setPhoto(url);
      // Persist the Pexels replacement so all components show the same image
      onActivityUpdate?.(act.id, { img: url });
      try { setDoc(doc(db, "activities", act.id), { img: url }, { merge: true }); }
      catch (e) { console.error("[ActivityCard] Failed to persist img:", e); }
    });
  }, [act.id, act.img]); // eslint-disable-line

  return (
    <div
      onClick={isAlt ? undefined : onClick}
      onTouchStart={() => { if (!isAlt) setPressed(true); }}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      style={{
        display: "flex",
        background: isAlt
          ? `linear-gradient(135deg, ${COLOR.altCardGradient}, ${COLOR.white})`
          : `linear-gradient(135deg, ${COLOR.warmSurface}, ${COLOR.white})`,
        border: `1px solid ${isAlt ? `${COLOR.mauve}22` : act.locked ? COLOR.lockedGreen : COLOR.borderWarm}`,
        borderRadius: 14,
        overflow: "hidden",
        cursor: isAlt ? "default" : "pointer",
        boxShadow: `0 1px 6px ${COLOR.inkDark}0f`,
        transform: pressed ? "scale(0.97)" : "scale(1)",
        opacity: pressed ? 0.82 : 1,
        transition: "transform 0.12s, opacity 0.12s",
        WebkitTapHighlightColor: "transparent",
        minHeight: 82,
      }}
    >
      {/* Thumbnail — 4:3 */}
      <div style={{ position: "relative", width: 120, height: 90, flexShrink: 0 }}>
        <Img
          src={photo}
          alt={act.title}
          style={{ width: "100%", height: "100%" }}
          objectPosition={act.imgCropParams ? `${act.imgCropParams.x}% ${act.imgCropParams.y}%` : undefined}
        />
        {act.locked && (
          <div style={{
            position: "absolute", top: 5, left: 5,
            background: `${COLOR.lockedGreen}d9`, borderRadius: 5,
            fontSize: 11, lineHeight: 1, padding: "3px 5px",
          }}>
            <Icon name="confirm" size={12} color={COLOR.white} />
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, padding: "10px 12px", display: "flex", flexDirection: "column", justifyContent: "space-between", minWidth: 0, overflow: "hidden" }}>
        {/* Top row: time + duration + AI badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: COLOR.mauve, fontFamily: FONT.mono, opacity: 0.9 }}>{act.time}</span>
          {act.duration && (
            <span style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, opacity: 0.8 }}>· {act.duration}</span>
          )}
          {act.aiGenerated && !act.locked && act.bookingStatus !== "booked" && (
            <div style={{
              display: "inline-flex", alignItems: "center",
              background: `${COLOR.mauve}26`, borderRadius: 4,
              fontSize: 9, fontFamily: FONT.mono, color: COLOR.mauveDark,
              lineHeight: 1, padding: "2px 5px",
            }}>
              <Icon name="aiSparkle" size={10} color={COLOR.mauveDark} style={{ marginRight: 2 }} /> AI
            </div>
          )}
        </div>

        {/* Title */}
        <div style={{
          fontSize: 14, fontWeight: 400, color: COLOR.inkDark,
          lineHeight: 1.25, marginBottom: 4,
          fontFamily: FONT.serif,
          ...(!isDesktop ? { overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" } : {}),
        }}>
          {act.title}
        </div>

        {/* Detail */}
        <div style={{
          fontSize: 12, color: COLOR.inkMid, lineHeight: 1.4,
          ...(!isDesktop ? { overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" } : {}),
        }}>
          {act.detail}
        </div>
      </div>

      {/* Badge column — category top, costRange below */}
      {(() => {
        const cat = act.category && CATEGORIES[act.category] ? act.category : null;
        const catEntry = cat ? CATEGORIES[cat] : { iconName: "imagePick", label: "Activity" };
        const cr = act.costRange || getCostRange(act.cost);
        const crText = cr === "free" ? "Free" : cr;
        const crSuffix = cat === "accommodation"
          ? <Icon name="accommodation" size={11} color={COLOR.inkMid} style={{ marginLeft: 3, verticalAlign: "middle" }} />
          : cat === "transport"
            ? <Icon name="transport" size={11} color={COLOR.inkMid} style={{ marginLeft: 3, verticalAlign: "middle" }} />
            : null;
        return (
          <div style={{
            display: !isDesktop ? "none" : "flex", flexDirection: "column", alignItems: "flex-end",
            justifyContent: "center", gap: 4,
            padding: "10px 10px 10px 0", flexShrink: 0,
            width: 90, overflow: "visible",
          }}>
            <span style={{
              fontSize: 10, fontFamily: FONT.mono,
              background: COLOR.categoryBadgeBg, color: COLOR.categoryBadgeText,
              padding: "2px 7px", borderRadius: 6, whiteSpace: "nowrap",
            }}>
              <Icon name={catEntry.iconName} size={11} color={COLOR.categoryBadgeText} style={{ marginRight: 3, verticalAlign: "middle" }} /> {catEntry.label}
            </span>
            <span style={{
              fontSize: 10, fontFamily: FONT.mono, letterSpacing: "0.05em",
              background: COLOR.warmSurface, color: COLOR.inkMid,
              padding: "2px 7px", borderRadius: 6, fontWeight: 700, whiteSpace: "nowrap",
            }}>
              {crText}{crSuffix}
            </span>
          </div>
        );
      })()}

      {/* Right column — arrow for alt, edit button for primary */}
      {isAlt ? (
        null
      ) : onEdit ? (
        <button
          onClick={e => { e.stopPropagation(); onEdit(onActivityUpdate); }}
          onTouchStart={e => e.stopPropagation()}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 44, flexShrink: 0, alignSelf: "stretch",
            background: "transparent", border: "none",
            borderLeft: `1px solid ${COLOR.borderWarm}`,
            color: COLOR.inkMid, fontSize: 15, cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
          title="Edit activity"
        >
          <Icon name="edit" size={14} color={COLOR.inkMid} />
        </button>
      ) : (
        <div style={{ display: "flex", alignItems: "center", paddingRight: 12, color: COLOR.mauve, fontSize: 20, opacity: 0.35, flexShrink: 0 }}>
          ›
        </div>
      )}
    </div>
  );
}
